import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DateTime, DurationLikeObject } from 'luxon';
import {
  LineChart,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Line,
  Tooltip,
  Legend,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Loading } from '@bumble/app/components/Loading';
import NoData from '@bumble/app/components/NoData';
import TimeRange from '@bumble/app/components/TimeRange';
import { CELLULAR_TIME_BUTTONS } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import { COLORS } from '@bumble/app/theme';
import { getTooltipLabel } from '@bumble/app/utils/get-date';

const colors = ['#ECC052', '#88847D', '#5F467E', '#7EB670'];

const containerStyle = {
  border: `4px solid ${COLORS.BACKGROUND}`,
  borderRadius: '8px'
};

interface CellularStatsProps {
  id: string;
}

const CellularStats = ({ id }: CellularStatsProps) => {
  const api = useApi();

  const [timeRange, setTimeRange] = useState<DurationLikeObject>(CELLULAR_TIME_BUTTONS[0].value);
  const [failOverCount, setFailOverCount] = useState<number>(0);

  const { isLoading, data, refetch, isRefetching } = useQuery(
    ['cellularStats', id, timeRange],
    api.getCellularStatsApiV1GetCellularStatsGet.bind(null, {
      cpe_id: id,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: true, cacheTime: 0 },
  );

  const { data: data2, refetch: refetch2 } = useQuery(
    ['cellularFailoverCount', id, timeRange],
    api.getCellularFailoverCountApiV1GetCellularFailoverCountGet.bind(null, {
      cpe_id: id,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: true, cacheTime: 0 },
  );

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) setTimeRange(val);
  };

  const handleRefreshClick = () => {
    refetch();
    refetch2();
    toast.success('Refreshed chart data.');
  };

  useEffect(() => {
    setFailOverCount((data2?.data as any)?.failover);
  }, [data2]);

  useEffect(() => {
    refetch();
    refetch2();
  }, [timeRange]);

  if (isLoading || isRefetching) {
    return <Loading />;
  }

  let originalData: any;

  if (data?.data) {
    originalData = data.data.map(n => ({
      ...n,
      time: Date.parse(n.timestamp)
    }));
  }

  function CustomizedTicks(props: any) {
    const { x, y, payload } = props;
    const convertedDate = typeof payload.value === 'string'
      ? new Date(payload.value).valueOf()
      : payload.value;
    const date = DateTime.fromMillis(convertedDate);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date?.toFormat('MMM d')}
          </tspan>
          <tspan textAnchor="middle" x="0" dy="20">
            {date?.toFormat('HH:mm')}
          </tspan>
        </text>
      </g>
    );
  }

  const series = [
    { key: 'lte.rssi.avg', label: 'LTE RSSI (dBm)' },
    { key: 'lte.snr.avg', label: 'LTE SNR (dB)' },
    { key: 'lte.rsrp.avg', label: 'LTE RSRP (dBm)' },
    { key: 'lte.rsrq.avg', label: 'LTE RSRQ (dB)' },
    { key: 'nr5g.rsrp.avg', label: 'NR5G RSRP (dBm)' },
    { key: 'nr5g.rsrq.avg', label: 'NR5G RSRQ (dB)' },
    { key: 'nr5g.snr.avg', label: 'NR5G SNR (dB)' },
    { key: ['tx.bytes.avg', 'rx.bytes.avg'], label: 'Network in/out (bytes/sec)' },
    // { key: '', label: 'Failover to Cellular Counts' },
  ];

  return (
    <Box sx={{ py: 2 }}>
      <TimeRange
        timeValues={CELLULAR_TIME_BUTTONS}
        selectedTime={timeRange}
        onTimeChange={handleTimeChange}
        onRefresh={handleRefreshClick}
        leftContent={
          <Typography variant="subtitle1" sx={{ ml: 2 }}>
            Failover Count:{' '}
            <span style={{
              color: 'rgb(31, 207, 57)',
              fontSize: '1.5em',
              fontWeight: 'bold',
            }}>
              {failOverCount}
            </span>
          </Typography>
        }
      />
      <Container>
        {id === undefined || !data || data?.data.length === 0 || !Array.isArray(data?.data) ? <NoData /> :
          <Grid container spacing={5} sx={{ marginTop: 1 }}>
            {series.map(({ label: seriesLabel, key }) => (
              <Grid item xs={12} md={6} key={seriesLabel}>
                <Box sx={containerStyle}>
                  <Typography variant="h5" sx={{ my: 2, textAlign: 'center' }}>
                    {seriesLabel}
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart
                      margin={{ top: 5, right: 10, left: 20, bottom: 5 }}
                      data={originalData}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        type="category"
                        allowDuplicatedCategory={false}
                        height={60}
                        interval={timeRange.days === 30 ? 20 : 10}
                        tick={<CustomizedTicks />}
                      />
                      <YAxis
                        orientation="left"
                        type="number"
                        domain={Array.isArray(key) ? [0, 5000] : [0, 100]}
                        width={30}
                        tickFormatter={(value: number) => `${new Intl.NumberFormat('en-US', {
                          notation: 'compact',
                          compactDisplay: 'short',
                        }).format(value)}`}
                      />
                      {Array.isArray(key) ? (
                        <>
                          <Line strokeWidth={2} stroke={colors[0]}dot={false} dataKey={key[0]} name={'Network out'} />
                          <Line strokeWidth={2} stroke={colors[2]} dot={false} dataKey={key[1]} name={'Network in'} />
                        </>
                      ) : <Line strokeWidth={2} dot={false} dataKey={key} name={seriesLabel} />}
                      {/* {id === 'diag' && (
                        <>
                          <Line
                            strokeWidth={2}
                            dot={false}
                            dataKey="value[0]"
                            name="Ambient Temperature"
                            stroke="#ECC052"
                          />
                          <Line
                            strokeWidth={2}
                            dot={false}
                            dataKey="value[1]"
                            name="CPU Temperature"
                            stroke="#5F467E"
                          />
                          <Line
                            strokeWidth={2}
                            dot={false}
                            dataKey="value[2]"
                            name="Disk Temperature"
                            stroke="#6F9C6F"
                          />
                          <Legend wrapperStyle={{ bottom: -5 }} iconType={'rect'} />
                        </>
                      )} */}
                      <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            ))}
          </Grid>
        }
      </Container>
    </Box>
  );
};

export default CellularStats;
